import { supabase } from '@/integrations/supabase/client';

/**
 * Product activity — what a signed-in user actually does.
 *
 * One call per action, fire-and-forget: the RPC stamps org + user from the
 * session, drops platform staff, and never throws into the UI. Page views
 * come from usePageTracking; logins from AuthContext; the wizard and the
 * activation actions (customer, mockup, estimate, proposal, Lumen) call this
 * at their single save points. Read on the super-admin Activity page.
 */
export type ProductEventType =
  | 'login'
  | 'page_view'
  | 'customer_created'
  | 'mockup_saved'
  | 'estimate_saved'
  | 'proposal_created'
  | 'proposal_sent'
  | 'proposal_emailed'
  | 'lumen_message'
  | 'training_booked'
  | `wizard_${string}`;

const SESSION_KEY = 'lm_activity_session';

/** Set to '1' to record activity under browser automation. See trackActivity. */
export const ACTIVITY_AUTOMATION_OPT_IN = 'lm_activity_automation';

function sessionId(): string | null {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/**
 * `login` once per browser session. onAuthStateChange re-fires SIGNED_IN on
 * token refresh and on tab focus, which would otherwise inflate the count
 * several times over for one visit.
 */
export function trackLoginOnce(): void {
  try {
    const key = 'lm_activity_login_logged';
    if (sessionStorage.getItem(key) === '1') return;
    sessionStorage.setItem(key, '1');
  } catch {
    /* private mode: fall through and record it */
  }
  trackActivity('login');
}

export function trackActivity(
  type: ProductEventType,
  opts: { path?: string; entityType?: string; entityId?: string | null; metadata?: Record<string, unknown> } = {},
): void {
  try {
    // Automation (the e2e suite) signs in as brand-new users; keep them out of
    // the feed so the numbers stay human. Opt back in the way the tours do —
    // localStorage 'lm_activity_automation' = '1' — to exercise this path.
    if (typeof navigator !== 'undefined' && navigator.webdriver) {
      let optedIn = false;
      try { optedIn = localStorage.getItem(ACTIVITY_AUTOMATION_OPT_IN) === '1'; } catch { /* private mode */ }
      if (!optedIn) return;
    }
    const path = opts.path ?? (typeof window !== 'undefined' ? window.location.pathname : undefined);
    // The page-view hook is mounted app-wide, so it also runs on the public
    // proposal / invoice / portal pages. The RPC would reject an anonymous
    // caller anyway; checking the in-memory session first keeps a homeowner
    // opening a proposal from making a pointless round trip.
    void supabase.auth.getSession().then(({ data }) => {
      if (!data.session) return;
      return supabase
      .rpc('track_product_event', {
        p_event_type: type,
        p_path: path ?? null,
        p_session_id: sessionId(),
        p_entity_type: opts.entityType ?? null,
        p_entity_id: opts.entityId ?? null,
        p_metadata: (opts.metadata ?? {}) as never,
      } as never)
      .then(({ error }) => {
        if (error && import.meta.env.DEV) console.warn('[activity]', type, error.message);
      });
    });
  } catch {
    /* never let analytics break a screen */
  }
}
